/**
 * Turns a lemma as the API returns it into the shape the renderers and the
 * lemma wizard work with.
 *
 * Two things the raw payload does not give directly:
 *
 * - Witness order and apparatus metadata. Each witness entry, attribution,
 *   variant reading and variant base reading carries `witnesses` (plain witness records, in no particular
 *   order) and `witness_joins` (one row per cited witness, ordered by the API by
 *   position then insertion, carrying lower apparatus, loci, relations…). The
 *   show page used to copy join metadata onto `witnesses` in place, so a saved
 *   "↓252 Syh" came back as "Syh ↓252", and a witness cited twice in one entry
 *   collapsed into one. The list is now built from the joins.
 *
 * - The order of what has no join: a variant's and a segment's notes, by
 *   their position.
 *
 * Pure: returns new objects and never mutates the payload.
 */

import {
  Attestation,
  AttestationTranslator,
  Attribution,
  CitedNote,
  Lemma,
  LemmaNote,
  NonGreek,
  Segment,
  Variant,
  VariantReading,
  WitnessEntry,
  WitnessJoinApi,
} from '@/hexapla-core/domains/lemmas/schema'
import { Witness } from '@/hexapla-core/domains/resources'
import { normalizeEntries } from '@/hexapla-core/domains/lemmas/witnessLines'

interface WithWitnessJoins {
  witnesses?: Witness[]
  witness_joins?: WitnessJoinApi[]
}

/**
 * The witnesses of an entry, reading or attribution, in apparatus order and
 * carrying their join metadata. Falls back to `witnesses` when no joins were
 * loaded (e.g. summary payloads).
 */
export const orderedWitnesses = (entity: WithWitnessJoins): Witness[] => {
  const joins = entity.witness_joins || []
  const witnesses = entity.witnesses || []
  if (!joins.length) return witnesses

  const byId = new Map(witnesses.map((w) => [w.id as number, w]))

  return joins
    .map((join) => {
      const id = join.witness_id ?? join.witness?.id
      const witness = (id !== undefined && id !== null && byId.get(id)) || (join.witness as Witness | undefined)
      if (!witness) return null

      return {
        ...witness,
        citation_id: join.id ?? null,
        citation_location: join.location || null,
        lower_apparatus: join.lower_apparatus || false,
        note: join.note || null,
        has_parens: join.has_parens || false,
        daughter_witness: join.daughter_witness || null,
        daughter_witness_id: join.daughter_witness_id ?? join.daughter_witness?.id ?? null,
        // The citation's own mark ("C^cat") wins over the witness record's.
        superscript: join.superscript || (witness as Witness & { superscript?: string | null }).superscript || null,
        loci: (join.loci || []).map((l) => l.locus),
        relations: join.relations || [],
      } as Witness
    })
    .filter((w): w is Witness => w !== null)
}

/**
 * The translators of a reading's head, in editorial order ("α′ θ′ σ′").
 *
 * The API returns them ordered by position, but a head being built in the wizard
 * has only the order of the list itself, so `position` leads and the list order
 * breaks ties. Never re-sorted by name: the order is a claim about the
 * manuscripts (Job 30:1d), not presentation.
 */
export const orderedTranslators = (attestation: Pick<Attestation, 'attestation_translators'>): AttestationTranslator[] =>
  inOrder(attestation.attestation_translators || [])

/** A list by `position`, the list's own order breaking ties. */
const inOrder = <T extends { position?: number | null }>(list: T[]): T[] =>
  list
    .map((item, index) => ({ item, index }))
    .sort((a, b) => (a.item.position ?? a.index) - (b.item.position ?? b.index) || a.index - b.index)
    .map(({ item }) => item)

/**
 * A variant's kind as the API stores it (`variants.kind`). One the wizard is
 * still building keeps its own: `null` until the editor picks one.
 */
export const withVariantKind = <V extends Variant>(variant: V): V => ({
  ...variant,
  kind: variant.kind === undefined ? 'reading' : variant.kind,
})

/** A note's witnesses, in apparatus order like every other citation list. */
const hydrateNote = (note: CitedNote): CitedNote => ({
  ...note,
  witnesses: orderedWitnesses({ witnesses: note.witnesses, witness_joins: note.citations }),
})

/** An owner's notes, in apparatus order. */
const hydrateNotes = (notes: CitedNote[] | undefined): CitedNote[] => inOrder(notes || []).map(hydrateNote)

const hydrateSegment = (segment: Segment): Segment => ({ ...segment, notes: hydrateNotes(segment.notes) })

const hydrateReading = (reading: VariantReading): VariantReading => ({
  ...reading,
  witnesses: orderedWitnesses(reading),
  segments: (reading.segments || []).map(hydrateSegment),
})

const hydrateVariant = (variant: Variant): Variant =>
  withVariantKind({
    ...variant,
    base_witnesses: orderedWitnesses({ witnesses: variant.base_witnesses, witness_joins: variant.base_witness_joins }),
    base_segments: (variant.base_segments || []).map(hydrateSegment),
    notes: hydrateNotes(variant.notes),
    readings: (variant.readings || []).map(hydrateReading),
  })

const hydrateEntry = <E extends WitnessEntry | Attribution>(entry: E): E => ({
  ...entry,
  witnesses: orderedWitnesses(entry),
  variants: (entry.variants || []).map(hydrateVariant),
})

const hydrateAttribution = (attribution: Attribution): Attribution => ({
  ...hydrateEntry(attribution),
  attribution_translators: inOrder(attribution.attribution_translators || []),
})

/**
 * A NonGr entry's citations. Entries made before citations name one witness
 * in `witness`; that stands as the single citation so every entry reads alike.
 */
const hydrateNonGreek = (ng: NonGreek): NonGreek => {
  const cited = orderedWitnesses(ng)
  return { ...ng, witnesses: cited.length ? cited : ng.witness ? [ng.witness] : [] }
}

const hydrateAttestation = (attestation: Attestation): Attestation => ({
  ...attestation,
  attestation_translators: orderedTranslators(attestation),
  // Wit lines and their segments, in apparatus order (domains/lemmas/witnessLines).
  witness_entries: normalizeEntries(attestation.witness_entries || []).map(hydrateEntry),
  attributions: (attestation.attributions || []).map(hydrateAttribution),
  variants: (attestation.variants || []).map(hydrateVariant),
  non_greek: (attestation.non_greek || []).map(hydrateNonGreek),
})

/** A lemma from the API, ready for the renderers and the wizard. */
export const hydrateLemma = (lemma: Lemma): Lemma => {
  if (!lemma?.attestations) return lemma
  return {
    ...lemma,
    attestations: lemma.attestations.map(hydrateAttestation),
    notes: inOrder<LemmaNote>(lemma.notes || []),
  }
}

export default { hydrateLemma, orderedWitnesses, orderedTranslators, withVariantKind }
